import React from "react";
import { Toast } from "flowbite-react";
import { HiCheck } from "react-icons/hi";

export interface SuccessToastProps {
  /**
   * Title of the success message
   */
  title: string;
  /**
   * Content of the success message
   */
  message?: string;
  /**
   * Whether the toast message should be displayed
   */
  show: boolean;
  /**
   * Function called when the toast message is closed
   */
  onClose: () => void;
  /**
   * Position of the toast message
   * @default "top-right"
   */
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
}

/**
 * SuccessToast - A reusable component for displaying success messages
 */
export const SuccessToast: React.FC<SuccessToastProps> = ({
  title,
  message,
  show,
  onClose,
  position = "top-right",
}) => {
  if (!show) return null;

  // Determine position based on position prop
  const positionClasses = {
    "top-right": "top-5 right-5",
    "top-left": "top-5 left-5",
    "bottom-right": "bottom-5 right-5",
    "bottom-left": "bottom-5 left-5",
  };

  return (
    <div className={`fixed ${positionClasses[position]} z-50`}>
      <Toast className="!bg-green-100 dark:!bg-green-700 !text-green-700 dark:!text-white min-w-[350px] max-w-[450px] shadow-lg">
        <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-500 text-white dark:bg-green-600 dark:text-white">
          <HiCheck className="h-6 w-6" />
        </div>
        <div className="ml-3 text-base font-medium mr-8">
          <p className="font-bold text-green-700 dark:text-white">{title}</p>
          {message && (
            <p className="text-green-700 dark:text-white">{message}</p>
          )}
        </div>
        <Toast.Toggle
          onClick={onClose}
          className="absolute top-2 right-2 bg-transparent text-green-700 hover:text-green-900 rounded-lg focus:ring-2 focus:ring-green-300 p-1.5 hover:bg-green-100/50 inline-flex h-8 w-8 dark:bg-transparent dark:text-white dark:hover:text-white dark:hover:bg-green-700/40"
        />
      </Toast>
    </div>
  );
};

export default SuccessToast;
